import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectOptions, selectConfig, selectLabels } from '../slices/configSlice';
import * as local from '../utils/constants';
import * as localLoan from '../utils/loanConstants';

// Surface shared constants from Redux (loan-service /v2/config) with a
// local-constant fallback so the UI never breaks if the fetch hasn't
// completed yet or the gateway is briefly unreachable.
//
// Each key on the returned object mirrors the legacy local-constant name so
// migrating a call site is a one-line swap:
//
//   - import { primaryBankOpts, sectorOpts } from 'utils/constants';
//   + const { primaryBankOpts, sectorOpts } = useConfigOptions();
//
// Memoized so consumers can safely destructure values into useEffect deps.
//
// For non-React modules (financials.js, paymentValues.js, loanProcessing.js)
// use the `utils/configAccess.getConfig()` helper instead — it reads from the
// same Redux store without requiring a component context.
export default function useConfigOptions() {
    const options = useSelector(selectOptions);
    const config  = useSelector(selectConfig);
    const labels  = useSelector(selectLabels);

    return useMemo(() => ({
        // ============================================================
        // Pricing & limits  (used by financials.js, loanProcessing.js,
        // paymentValues.js, scoring modules)
        // ============================================================
        processingfees:           config?.processingfees           ?? localLoan.processingfees,
        loanInterestRate:         config?.loanInterestRate         ?? localLoan.interestRate,
        loanAmtLimits:            config?.loanAmtLimits            ?? localLoan.loanAmtLimits,
        loanPricing:              config?.loanPricing              ?? localLoan.loanPricing,
        perMonthPaymentFractions: config?.perMonthPaymentFractions ?? localLoan.perMonthPaymentFractions,
        riskRates:                config?.riskRates                ?? localLoan.riskRates,
        scoreToRatingMins:        config?.scoreToRatingMins        ?? localLoan.scoreToRatingMins,
        creditScoreThresholds:    config?.creditScoreThresholds    ?? localLoan.creditScoreThresholds,
        repaymentAmtLimit:        config?.repaymentAmtLimit        ?? local.repaymentAmtLimit,

        // ============================================================
        // Loan products & lifecycle
        // ============================================================
        loanTypes:     config?.loanTypes     ?? local.loanTypes,
        loanTypesOld:  config?.loanTypesOld  ?? local.loanTypesOld,
        loanStates:    config?.loanStates    ?? local.loanStates,
        loanStatesOld: config?.loanStatesOld ?? local.loanStatesOld,
        loanStages:    config?.loanStages    ?? local.loanStages,
        // Loan product catalog served by /v2/config, filtered to what staff
        // is allowed to see. Fallback pipes the local loanFilters array
        // through the same visibility filter so cold-start behaves the same.
        loanProducts:  (config?.loanProducts ?? local.loanFilters?.map(p => ({ ...p, visibleStaff: true })))
                            ?.filter(p => p.visibleStaff !== false),
        loanFilters:   local.loanFilters,            // legacy alias — will be removed once all consumers migrate
        loanRejectionReasons: localLoan.loanRejectionReasons, // static admin-curated list; stays local
        adminRoles:    config?.adminRoles    ?? local.adminRoles,

        // ============================================================
        // Form option lists  (display data)
        // ============================================================
        // pre-existing
        primaryBankOpts:    options?.primaryBanks     ?? local.primaryBankOpts,
        momoProviderOpts:   options?.momoProviders    ?? local.momoProviderOpts,
        sectorOpts:         options?.sectors          ?? local.sectorOpts,
        pensionCompanies:   options?.pensionCompanies ?? local.pensionCompanies,
        insuranceOpts:      options?.insurance        ?? local.insuranceOpts,
        purposeOfLoanOpts:  options?.purposeOfLoan    ?? local.purposeOfLoanOpts,
        billPaymentOptions: options?.billPayment      ?? local.billPaymentOptions,

        // identity / general profile
        optionsYN:           options?.yesNo            ?? local.optionsYN,
        education:           options?.education        ?? local.education,
        genderRoles:         options?.genderRoles      ?? local.genderRoles,
        nations:             local.nations,                                       // stays local (lookup helper)
        networkProviders:    options?.networkProviders ?? local.networkProviders,
        maritalStatusOpts:   options?.maritalStatus    ?? local.maritalStatusOpts,
        idCards:             options?.idCards          ?? local.idCards,
        workStates:          options?.workStates       ?? local.workStates,

        // employment
        employment:             options?.employment             ?? local.employment,
        numOfYearsWithEmployer: options?.numOfYearsWithEmployer ?? local.numOfYearsWithEmployer,
        employmentLevelOpts:    options?.employmentLevel        ?? local.employmentLevelOpts,
        employmentTypeOpts:     options?.employmentType         ?? local.employmentTypeOpts,
        employmentNatureOpts:   options?.employmentNature       ?? local.employmentNatureOpts,

        // income / payments
        incomeFrequencyOpts:  options?.incomeFrequency  ?? local.incomeFrequencyOpts,
        automaticPaymentOpts: options?.automaticPayment ?? local.automaticPaymentOpts,
        paymentPlanOpts:      options?.paymentPlan      ?? local.paymentPlanOpts,
        paymentDurationOpts:  options?.paymentDuration  ?? local.paymentDurationOpts,
        missedPaymentOptions: options?.missedPayment    ?? local.missedPaymentOptions,
        billsHistoryOpts:     options?.billsHistory     ?? local.billsHistoryOpts,
        balanceOpts:          options?.balance          ?? local.balanceOpts,

        // pension
        avgPensionContOpts:    options?.avgPensionCont    ?? local.avgPensionContOpts,
        pensionContYrsOpts:    options?.pensionContYrs    ?? local.pensionContYrsOpts,
        pensionContOneYrOpts:  options?.pensionContOneYr  ?? local.pensionContOneYrOpts,
        pensionCollateralOpts: options?.pensionCollateral ?? local.pensionCollateralOpts,
        pensionStateOpts:      options?.pensionState      ?? local.pensionStateOpts,
        pensionLoanType:       options?.pensionLoanType   ?? local.pensionLoanType,
        relPensionTrusteeOpts: options?.relPensionTrustee ?? local.relPensionTrusteeOpts,

        // investment / savings / accounts
        collateralOpts:           options?.collateral           ?? local.collateralOpts,
        investmentSizeOpts:       options?.investmentSize       ?? local.investmentSizeOpts,
        investmentFrequencyOpts:  options?.investmentFrequency  ?? local.investmentFrequencyOpts,
        investmentLoanOpts:       options?.investmentLoan       ?? local.investmentLoanOpts,
        savingsSizeOpts:          options?.savingsSize          ?? local.savingsSizeOpts,
        savingsFrequencyOpts:     options?.savingsFrequency     ?? local.savingsFrequencyOpts,
        transactionFrequencyOpts: options?.transactionFrequency ?? local.transactionFrequencyOpts,
        transactionSizeOpts:      options?.transactionSize      ?? local.transactionSizeOpts,
        accountAgeOpts:           options?.accountAge           ?? local.accountAgeOpts,
        accountActiveOpts:        options?.accountActive        ?? local.accountActiveOpts,
        accountBalanceOpts:       options?.accountBalance       ?? local.accountBalanceOpts,
        transFrequencyOpts:       options?.transFrequency       ?? local.transFrequencyOpts,
        consistentOpts:           options?.consistent           ?? local.consistentOpts,
        momoTransactionOpts:      options?.momoTransaction      ?? local.momoTransactionOpts,
        yearsInBusinessOpts:      options?.yearsInBusiness      ?? local.yearsInBusinessOpts,
        transPerMonthOpts:        options?.transPerMonth        ?? local.transPerMonthOpts,
        minimumAmtPerTransOpts:   options?.minimumAmtPerTrans   ?? local.minimumAmtPerTransOpts,
        averageBalanceOpts:       options?.averageBalance       ?? local.averageBalanceOpts,
        incomeConsistencyOpts:    options?.incomeConsistency    ?? local.incomeConsistencyOpts,
        plocOpts:                 options?.ploc,
        plocYearsInBusinessOpts:  options?.plocYearsInBusiness  ?? local.plocYearsInBusinessOpts,
        payrollLoanOpts:          options?.payrollLoan          ?? local.payrollLoanOpts,
        salariedOpts:             options?.salaried             ?? local.salariedOpts,

        // business sector / KYC
        customerTypeOpts:     options?.customerType     ?? local.customerTypeOpts,
        otherFinancialOpts:   options?.otherFinancial   ?? local.otherFinancialOpts,
        businessTypeOpts:     options?.businessType     ?? local.businessTypeOpts,
        businessActivityOpts: options?.businessActivity ?? local.businessActivityOpts,
        businessSectorOpts:   options?.businessSector   ?? local.businessSectorOpts,
        demandGoodsOpts:      options?.demandGoods      ?? local.demandGoodsOpts,

        // reporting / collections
        collectionStates: options?.collectionStates ?? local.collectionStates,
        reportTypes:      options?.reports          ?? local.reportTypes,
        otherReportTypes: options?.otherReports     ?? local.otherReportTypes,

        // staff / admin
        relationshipManagers:       config?.relationshipManagers       ?? local.relationshipManagers,
        relationshipManagersOutput: config?.relationshipManagersOutput,
        adminRolesSelect:           config?.adminRolesSelect           ?? local.adminRolesSelect,

        // ============================================================
        // Display-only label maps  (value → human label)
        // ============================================================
        balanceOptsOutput:            labels?.balance,
        billsHistoryOptsOutput:       (labels?.billsHistory          ?? local.billsHistoryOpts), // alias
        missedPaymentOptionsOutput:   labels?.missedPayment          ?? local.missedPaymentOptions,
        numOfYearsWithEmployerOutput: labels?.numOfYearsWithEmployer,
        sectorOptsOutput:             labels?.sectors,
    }), [options, config, labels]);
}
